import React, { useState } from "react";
import { ToastContainer } from "react-toastify";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import { makeRequest } from "../../../_config/api";
import { countryCodes } from "./CountryCode";

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    countryCode: "+1",
    reference: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const errs = {};

    if (!formData.name.trim()) errs.name = "Required";
    if (!formData.email) errs.email = "Required";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      errs.email = "Invalid email";

    if (!formData.phone.trim()) errs.phone = "Required";
    else if (!/^[0-9\s\-+]{7,15}$/.test(formData.phone.replace(/\s/g, "")))
      errs.phone = "7-15 digits only";

    if (!formData.subject.trim()) errs.subject = "Required";
    if (!formData.message.trim()) errs.message = "Required";

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      showErrorToast("Please fix the errors");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        reference: formData.reference.trim(),
        countryCodes: formData.countryCode,
        subject: formData.subject.trim(),
        message: formData.message.trim(),
      };

      await makeRequest("POST", "/user/contactUsSave", payload);
      showSuccessToast("Message sent successfully! 🎉");

      setFormData({
        name: "",
        email: "",
        countryCode: "+1",
        phone: "",
        reference: "",
        subject: "",
        message: "",
      });
      setErrors({});
    } catch (err) {
      showErrorToast(err?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-12 sm:py-16 md:py-24 bg-gray-50">
      <ToastContainer position="top-center" autoClose={4000} />

      {/* Loader */}
      {loading && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-xl flex flex-col items-center">
            <div className="h-10 w-10 sm:h-12 sm:w-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
            <p className="mt-4 text-sm sm:text-base font-medium text-gray-700">
              Sending...
            </p>
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        {/* Heading */}
        <div className="text-center mb-10 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-gray-900">
            Begin Your Journey With Us!
          </h2>
          <p className="mt-3 text-sm sm:text-base md:text-lg text-gray-600">
            We're excited to hear from you. Drop us a message!
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-7">
          {/* Name + Email */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {/* Name */}
            <div className="relative">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`peer w-full h-[52px] sm:h-[58px] px-4 sm:px-5 pt-6 pb-2 border ${errors.name ? "border-red-500" : "border-gray-300"
                  } rounded-lg outline-none focus:border-red-500`}
                placeholder=" "
              />
              <label className={`absolute left-4 sm:left-5 top-4 text-sm sm:text-base text-gray-500 transition-all
                ${formData.name ? "text-xs top-2 text-red-500" : "peer-focus:text-xs peer-focus:top-2 peer-focus:text-red-500"}`}>
                Your Full Name*
              </label>
              {errors.name && (
                <p className="mt-1 text-xs text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Email */}
            <div className="relative">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                autoComplete="email"
                className={`peer w-full h-[52px] sm:h-[58px] px-4 sm:px-5 pt-6 pb-2 border ${errors.email ? "border-red-500" : "border-gray-300"
                  } rounded-lg outline-none focus:border-red-500`}
                placeholder=" "
              />
              <label className={`absolute left-4 sm:left-5 top-4 text-sm sm:text-base text-gray-500 transition-all
                ${formData.email ? "text-xs top-2 text-red-500" : "peer-focus:text-xs peer-focus:top-2 peer-focus:text-red-500"}`}>
                Email Address*
              </label>
              {errors.email && (
                <p className="mt-1 text-xs text-red-600">{errors.email}</p>
              )}
            </div>
          </div>

          {/* Phone */}
          <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-4">
            {/* Country Code */}
            <div className="relative">
              <select
                name="countryCode"
                value={formData.countryCode}
                onChange={handleChange}
                className="w-full h-[52px] sm:h-[58px] px-3 border border-gray-300 rounded-lg bg-white outline-none focus:border-red-500"
              >
                {countryCodes.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </select>
              <label className="absolute -top-2 left-3 bg-white px-1 text-xs text-gray-500">
                Country Code
              </label>
            </div>

            {/* Phone Number */}
            <div className="relative">
              <input
                type="tel"
                inputMode="numeric"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`peer w-full h-[52px] sm:h-[58px] px-4 sm:px-5 pt-6 pb-2 border ${errors.phone ? "border-red-500" : "border-gray-300"
                  } rounded-lg outline-none focus:border-red-500`}
                placeholder=" "
              />
              <label className={`absolute left-4 sm:left-5 top-4 text-sm sm:text-base text-gray-500 transition-all
                ${formData.phone ? "text-xs top-2 text-red-500" : "peer-focus:text-xs peer-focus:top-2 peer-focus:text-red-500"}`}>
                Phone Number*
              </label>
              {errors.phone && (
                <p className="mt-1 text-xs text-red-600">{errors.phone}</p>
              )}
            </div>
          </div>

          {/* Reference */}
          <div className="relative">
            <input
              type="text"
              name="reference"
              value={formData.reference}
              onChange={handleChange}
              className={`peer w-full h-[52px] sm:h-[58px] px-4 sm:px-5 pt-6 pb-2 border ${errors.reference ? "border-red-500" : "border-gray-300"
                } rounded-lg outline-none focus:border-red-500`}
              placeholder=" "
            />
            <label className={`absolute left-4 sm:left-5 top-4 text-sm sm:text-base text-gray-500 transition-all
              ${formData.reference ? "text-xs top-2 text-red-500" : "peer-focus:text-xs peer-focus:top-2 peer-focus:text-red-500"}`}>
              Reference*
            </label>
            {errors.reference && (
              <p className="mt-1 text-xs text-red-600">{errors.reference}</p>
            )}
          </div>

          {/* Subject */}
          <div className="relative">
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              className={`peer w-full h-[52px] sm:h-[58px] px-4 sm:px-5 pt-6 pb-2 border ${errors.subject ? "border-red-500" : "border-gray-300"
                } rounded-lg outline-none focus:border-red-500`}
              placeholder=" "
            />
            <label className={`absolute left-4 sm:left-5 top-4 text-sm sm:text-base text-gray-500 transition-all
              ${formData.subject ? "text-xs top-2 text-red-500" : "peer-focus:text-xs peer-focus:top-2 peer-focus:text-red-500"}`}>
              Subject*
            </label>
            {errors.subject && (
              <p className="mt-1 text-xs text-red-600">{errors.subject}</p>
            )}
          </div>

          {/* Message */}
          <div className="relative">
            <textarea
              name="message"
              rows={5}
              value={formData.message}
              onChange={handleChange}
              className={`peer w-full px-4 sm:px-5 pt-6 pb-2 border ${errors.message ? "border-red-500" : "border-gray-300"
                } rounded-lg outline-none focus:border-red-500 resize-none`}
              placeholder=" "
            />
            <label className={`absolute left-4 sm:left-5 top-4 text-sm sm:text-base text-gray-500 transition-all
              ${formData.message ? "text-xs top-2 text-red-500" : "peer-focus:text-xs peer-focus:top-2 peer-focus:text-red-500"}`}>
              Your Message*
            </label>
            {errors.message && (
              <p className="mt-1 text-xs text-red-600">{errors.message}</p>
            )}
          </div>

          {/* Submit */}
          <div className="pt-4 text-center">
            <button
              type="submit"
              disabled={loading}
              className="w-full sm:w-auto min-w-[220px] bg-red-500 hover:bg-red-600 text-white px-8 py-3.5 rounded-lg font-medium text-base sm:text-lg shadow-md transition disabled:opacity-60"
            >
              {loading ? "Sending..." : "Send Message →"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default ContactForm;
